<template>
  <div
    class="item-maker-cart"
    @click="$emit('item-clicked')"
    @keydown.enter="$emit('item-clicked')"
    @keydown.space.prevent="$emit('item-clicked')"
    role="button"
    tabindex="0"
    :aria-label="`View details for ${props.item.makercart_activity}`"
  >
    <div
      class="item-content"
      @mouseover="handleMouseOver"
      @mouseleave="handleMouseLeave"
    >
      <div class="image-container">
        <img
          :src="props.item.image"
          :alt="generateImageAltText(props.item.makercart_activity)"
          class="item-image"
          :id="'item-image-' + props.item.id"
          @error="handleImageError"
        />
        <div class="overlay"></div>

        <!-- Badges -->
        <div class="badges-container">
          <Badge v-if="props.item.is_free" variant="blue-light" label="Free" />
          <Badge
            v-if="props.item.popular"
            variant="yellow-light"
            label="Popular"
          />
        </div>
        <div class="like-icon-container" v-if="canLike">
          <img
            v-if="isLiked && props.isLogged !== false"
            height="40"
            src="/images/liked-01.svg"
            alt="Remove from favourites"
            class="like-icon"
            @click.stop="handleLikeClick(props.item.makercart_id)"
            @keydown.enter.stop="handleLikeClick(props.item.makercart_id)"
            @keydown.space.prevent.stop="
              handleLikeClick(props.item.makercart_id)
            "
            role="button"
            tabindex="0"
          />
          <img
            v-else-if="!isLiked && props.isLogged !== false"
            height="40"
            src="/images/like-01.svg"
            alt="Add to favourites"
            class="like-icon"
            @click.stop="handleLikeClick(props.item.makercart_id)"
            @keydown.enter.stop="handleLikeClick(props.item.makercart_id)"
            @keydown.space.prevent.stop="
              handleLikeClick(props.item.makercart_id)
            "
            role="button"
            tabindex="0"
          />
          <img
            v-else
            height="30"
            src="/images/like-brown.svg"
            alt=""
            class="like-icon"
            style="cursor: not-allowed"
          />
        </div>
      </div>
      <div class="item-info-container" ref="infoContainer">
        <div class="item-info" ref="itemInfo">
          <div class="title-section">
            {{ props.item.makercart_activity }}
          </div>
        </div>

        <div class="line-breaker"></div>

        <div class="grade-info">
          <span class="grade-label">Grade:</span>
          <span class="grade-value">{{ formatGradeLevel }}</span>
        </div>

        <div class="subject-duration-row">
          <div class="subject-info">
            <span class="subject-label">Subject:</span>
            <span class="subject-value">{{ props.item.subject }}</span>
          </div>
          <div class="duration-info">
            <span class="duration-label">Duration:</span>
            <span class="duration-value">{{ props.item.duration }}</span>
          </div>
        </div>

        <div class="tags-section">
          <div
            v-for="(tag, index) in displayTags"
            :key="index"
            class="item-tag"
          >
            {{ tag }}
          </div>
          <div v-if="remainingTagsCount > 0" class="more-tags">
            {{ remainingTagsCount }}+ more
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable */

import { defineProps, ref, computed, defineEmits, watch } from "vue";
import StemStudioService from "@/services/stemStudioService";
import { generateImageAltText } from "@/utils/imageUtils";
import Badge from "@/components/common/Badge.vue";

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  isLogged: {
    type: Boolean,
    default: true,
  },
  canLike: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(["update-liked-status"]);

const infoContainer = ref(null);
const itemInfo = ref(null);

const isHovered = ref(false);
const isLiked = ref(props.item.liked || false);

watch(
  () => props.item.liked,
  (newValue) => {
    isLiked.value = newValue || false;
  }
);

const maxLength = 40; // Ganti sesuai kebutuhan

const truncatedText = computed(() => {
  const activity = props.item.makercart_activity;
  if (activity.length > maxLength && !isHovered.value) {
    return activity.slice(0, maxLength) + "...";
  }
  return activity;
});

const displayText = computed(() => {
  return truncatedText.value;
});

const formatGradeLevel = computed(() => {
  if (!props.item.grade_level || props.item.grade_level.length === 0) {
    return "";
  }
  return props.item.grade_level.join(", ");
});

const displayTags = computed(() => {
  if (!props.item.tags || props.item.tags.length === 0) {
    return [];
  }
  return props.item.tags.slice(0, 3);
});

const remainingTagsCount = computed(() => {
  if (!props.item.tags || props.item.tags.length <= 3) {
    return 0;
  }
  return props.item.tags.length - 3;
});

const handleMouseOver = () => {
  isHovered.value = true;
};

const handleMouseLeave = () => {
  isHovered.value = false;
};

const handleLikeClick = async (id) => {
  const previousLikedState = isLiked.value;
  isLiked.value = !isLiked.value;

  try {
    const response = await StemStudioService.likeMakercart(id);
    if (response.status === 200) {
      isLiked.value = response.data.data.liked;
      emit("update-liked-status", { id, liked: response.data.data.liked });
    }
  } catch (err) {
    isLiked.value = previousLikedState;
    alert("Error posting cart data:", err.message);
  }
};

// Fallback untuk gambar yang gagal dimuat
const handleImageError = (event) => {
  event.target.src = "./images/content-placeholder.svg"; // Ganti dengan placeholder image
};
</script>

<style scoped>
.item-maker-cart {
  text-align: center;
  background-color: #f2f3fb;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  height: 450px;
}

.item-maker-cart:hover {
  transform: translateY(-5px);
}

.item-content {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  position: relative;
}

.image-container {
  width: 100%;
  position: relative;
  height: 90%;
  overflow: hidden;
}

.item-image {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: filter 0.3s ease;
  z-index: 1;
}

.overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.6);
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
  z-index: 2;
}

.item-maker-cart:hover .overlay {
  opacity: 1;
}

.item-info-container {
  position: absolute;
  bottom: 0;
  width: 100%;
  padding: 1rem;
  padding-bottom: 40px;
  height: 250px;
  z-index: 3;
  background-color: #f2f3fb;
  border-radius: 0 0 12px 12px;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  transform: translateY(calc(100% - 110px));
  transition:
    transform 0.4s ease,
    padding-bottom 0.4s ease;
}

.line-breaker {
  width: 100%;
  height: 1px;
  border-bottom: 2px dashed #98a2b3;
  margin: 4px 0;
  opacity: 0;
}

.item-maker-cart:hover .line-breaker {
  opacity: 1;
}

.item-maker-cart:hover .item-info-container {
  transform: translateY(0px);
  padding-bottom: 1rem;
}

.item-info-container::after {
  content: "";
  position: absolute;
  top: -25px;
  left: -25px;
  right: -25px;
  height: 20%;
  background-color: #f2f3fb;
  opacity: 0;
  filter: blur(8px);
  z-index: 5;
  transition: opacity 0.3s ease;
}

.item-maker-cart:hover .item-info-container::after {
  opacity: 1;
}

.item-info {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 0;
  z-index: 6;
}

.title-section {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-xxl);
  font-weight: 700;
  line-height: 1.4;
  color: var(--primary-blue);
  text-align: left;
  word-break: break-word;
  flex: 1;
  margin-right: 0.5rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.grade-info {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  font-family: "League Spartan", sans-serif;
  font-size: 0.875rem;
  line-height: 1.25;
  opacity: 0;
  transform: translateY(20px);
  transition:
    opacity 0.4s ease,
    transform 0.4s ease;
  transition-delay: 0.1s;
}

.item-maker-cart:hover .grade-info {
  opacity: 1;
  transform: translateY(0);
}

.grade-label {
  font-weight: 600;
  color: #40444b;
}

.grade-value {
  font-weight: 400;
  color: #3b4648;
}

.subject-duration-row {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  font-family: "League Spartan", sans-serif;
  font-size: 0.875rem;
  line-height: 1.25;
  opacity: 0;
  transform: translateY(20px);
  transition:
    opacity 0.4s ease,
    transform 0.4s ease;
  transition-delay: 0.2s;
}

.item-maker-cart:hover .subject-duration-row {
  opacity: 1;
  transform: translateY(0);
}

.subject-info,
.duration-info {
  display: flex;
  gap: 0.25rem;
}

.subject-label,
.duration-label {
  font-weight: 600;
  color: #40444b;
}

.subject-value,
.duration-value {
  font-weight: 400;
  color: #3b4648;
}

.tags-section {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.25rem;
  opacity: 0;
  transform: translateY(20px);
  transition:
    opacity 0.4s ease,
    transform 0.4s ease;
  transition-delay: 0.3s;
}

.item-maker-cart:hover .tags-section {
  opacity: 1;
  transform: translateY(0);
}

.item-tag {
  background-color: #ffffff;
  border-radius: 4px;
  padding: 0.25rem 0.75rem;
  font-family: "League Spartan", sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  color: #40444b;
  border: 1px solid var(--neutral-100);
  white-space: nowrap;
}

.more-tags {
  border-radius: 20px;
  padding: 0.25rem 0.75rem;
  font-family: "League Spartan", sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  color: #3b4648;
  white-space: nowrap;
}

.like-icon {
  margin-left: 10px;
  z-index: 7;
  cursor: pointer;
  transition: transform 0.2s ease-in-out;
}

.like-icon:hover {
  transform: scale(1.1);
  filter: brightness(0.9);
}

.like-icon-container {
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 10;
}

/* Badges positioning */
.badges-container {
  position: absolute;
  top: 1rem;
  left: 1rem;
  display: flex;
  gap: 0.5rem;
  z-index: 10;
}

.item-maker-cart:hover .badges-container :deep(.badge) {
  transform: scale(1.05);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3);
}

/* @media (max-width: 1200px) {
  .image-container {
    height: 100%;
  }

  .title-section {
    -webkit-line-clamp: 1;
    line-clamp: 1;
  }

  .item-info-container {
    height: clamp(220px, 14vw, 250px);
    transform: translateY(calc(100% - clamp(68px, 4.8vw, 80px)));
  }
}

@media (max-width: 1800px) {
  .title-section {
    -webkit-line-clamp: 1;
    line-clamp: 1;
  }

  .item-info-container {
    height: clamp(100px, 14vw, 250px);
    transform: translateY(calc(100% - clamp(68px, 4.8vw, 80px)));
  }
} */

@media (max-width: 768px) {
  .item-maker-cart {
    height: 250px;
  }

  .item-info-container {
    z-index: 20;
    transform: translateY(calc(100% - 50px)) !important;
    padding-bottom: 40px !important;
    pointer-events: auto;
  }

  .title-section {
    -webkit-line-clamp: 1;
    line-clamp: 1;
    font-size: var(--fs-small);
  }

  .image-container {
    padding-bottom: 100%;
  }

  .item-image {
    top: 25%;
    transform: translate(0, -50%);
  }

  .badges-container {
    display: flex;
    flex-direction: column;
    gap: 0px;
    top: 5px;
    left: 5px;
    z-index: 1;
  }

  .badges-container :deep(.badge) {
    font-size: 12px;
  }

  .like-icon-container {
    top: 5px;
    right: 5px;
    z-index: 1;
  }

  .subject-duration-row {
    flex-direction: column;
    gap: 0px;
  }

  /* Disable all hover transitions on mobile */
  .item-maker-cart:hover {
    transform: none;
  }

  .item-maker-cart:hover .overlay {
    opacity: 0;
  }

  .item-maker-cart:hover .line-breaker {
    opacity: 0;
  }

  .item-maker-cart:hover .grade-info {
    opacity: 0;
    transform: translateY(20px);
  }

  .item-maker-cart:hover .subject-duration-row {
    opacity: 0;
    transform: translateY(20px);
  }

  .item-maker-cart:hover .tags-section {
    opacity: 0;
    transform: translateY(20px);
  }

  .item-maker-cart:hover .badges-container :deep(.badge) {
    transform: none;
    box-shadow: none;
  }

  .item-maker-cart:hover .item-info-container::after {
    opacity: 0;
  }
}
</style>
